import * as React from "react";
import { FunctionComponent } from "react";
import Select, { components } from "react-select";
import type {
  Props,
  ContainerProps,
  SelectComponentsConfig,
  GroupBase,
} from "react-select";
import Creatable from "react-select/creatable";
import Async from "react-select/async";
import type { AsyncProps } from "react-select/async";
import AsyncCreatable from "react-select/async-creatable";
import {
  theme,
  styled,
  unit,
  hsla,
  scaleFontSize,
} from "../../styled-components";
import { Icon } from "../Icon/Icon";
import { InputIcon } from "../Input/Input";
import {
  SELECT_DEFAULTS,
  INPUT_ICON_DEFAULTS,
  INPUT_BORDER_COLOR,
} from "../Input/variables";
import { Label, LabelActive } from "../Label/Label";
import { Block } from "../Block/Block";
import { IconPositionType, FieldError } from "../Input/shared";

const { SelectContainer, Option, Menu, Control, ValueContainer } = components;

interface ISelectCustomProps {
  primitiveValue?: boolean;
  withIcon?: boolean;
  icon?: React.ReactComponentElement<typeof Icon>;
  title?: string;
  alwaysPadding?: boolean;
  ignorePadding?: boolean;
  withDescription?: boolean;
  optionWithIcon?: boolean;
  scaleAmount?: number;
  errorMessage?: any;
  labelPadding?: number;
  menuTitle?: string;
  opacity?: number;
  optionWithIconGridTemplate?: string;
  optionWithGridGap?: number;
}
interface ISelectProps extends Props, ISelectCustomProps {}
interface IAsyncSelectProps
  extends AsyncProps<any, boolean, GroupBase<any>>,
    ISelectCustomProps {}

type OptionType = any;

const SelectOptionStyle = (
  base: React.CSSProperties,
  { isFocused, isSelected }: any
) => {
  return {
    ...base,
    backgroundColor: isSelected
      ? theme.colors.gray.lightest
      : isFocused
      ? hsla(theme.colors.gray.lightest, 0.45)
      : theme.colors.white.base,
    "&:active": {
      backgroundColor: theme.colors.gray.lightest,
    },
    color: theme.colors.gray.darker, // isSelected ? theme.colors.white.base : theme.colors.gray.darker,
    borderRadius: theme.borderRadius,
    margin: "0 8px",
    padding: "8px",
    width: "calc(100% - 16px)",
  };
};
const SelectControlStyles = (
  base: React.CSSProperties,
  { isFocused, selectProps, errorMessage }: any
) => ({
  ...base,
  minHeight: selectProps.scaleAmount
    ? theme.element.height / selectProps.scaleAmount
    : theme.element.height,
  borderColor: selectProps.errorMessage
    ? theme.colors["alert"].base
    : isFocused
    ? INPUT_BORDER_COLOR
    : INPUT_BORDER_COLOR,
  backgroundColor: "transparent",
  boxShadow: "none !important",
  zIndex: 20000,
  "&:hover": { borderColor: null },
});

const SelectInputStyles = (base: React.CSSProperties) => ({
  ...base,
  marginLeft: 0,
  marginTop: 0,
  marginBottom: 0,
  color: theme.colors.gray.darker,
});

const SelectValueContainerStyles = (base: React.CSSProperties, props: any) => {
  return {
    ...base,
    /* subtract  border-top & border-bottom width */
    minHeight: props.selectProps.scaleAmount
      ? (theme.element.height - 2) / props.selectProps.scaleAmount
      : theme.element.height - 2,
    // @TODO Use InputIconPaddingLeft in input.tsx
    paddingLeft: props.selectProps.withIcon
      ? props.selectProps.scaleAmount
        ? // withIcon + scaleAmount
          (theme.element.padding.large + INPUT_ICON_DEFAULTS.size) /
          props.selectProps.scaleAmount
        : // withIcon
          theme.element.padding.large + INPUT_ICON_DEFAULTS.size
      : props.selectProps.scaleAmount
      ? // withoutIcon + scaleAmount
        theme.element.padding.large / props.selectProps.scaleAmount
      : // withoutIcon and without scaleAmount
        theme.element.padding.large,
    fontSize: props.selectProps.scaleAmount
      ? scaleFontSize(theme.fontSize.large, props.selectProps.scaleAmount)
      : theme.fontSize.large,
    // without this the value/input split the row 50/50 into two "auto"
    // columns instead of the value getting full width - was truncating
    // things like "Most recent" way too early
    gridTemplateColumns: "minmax(0, 1fr)",
    // was overflow: hidden by default, clipping anything that spilled past this box
    overflow: "visible",
  };
};

const SelectStyles: any = {
  control: SelectControlStyles,
  input: (base: React.CSSProperties, state: any) => SelectInputStyles(base),
  valueContainer: SelectValueContainerStyles,
  option: SelectOptionStyle,
  menuList: (base: any) => ({ ...base, margin: "8px 0" }),
  placeholder: (base: any) => {
    return {
      ...base,
      color: "inherit",
      opacity: 0,
    };
  },
  singleValue: (base: any, { selectProps }: any) => ({
    ...base,
    top: "65%",
    padding: "10px 0",
    marginLeft: 0,
    color: theme.colors.gray.darker,
    fontSize: selectProps.scaleAmount
      ? scaleFontSize(theme.fontSize.large, selectProps.scaleAmount)
      : theme.fontSize.large,
    // same - let a slightly-too-wide value spill instead of hard-clipping
    overflow: "visible",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: theme.header.zIndex + 20000000,
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    opacity: 0,
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    paddingLeft: 10,
    paddingRight: 10,
  }),
};

export const SelectContainerExtendedStyled = styled(SelectContainer)<{
  withIcon?: boolean;
  hasValue?: boolean;
  ignorePadding?: boolean;
  alwaysPadding?: boolean;
  labelPadding?: number;
  scaleAmount?: number;
  errorMessage?: any;
}>`
  &.app-select--is-disabled {
    opacity: 0.5;
  }

  ${({ errorMessage }) => (errorMessage ? `margin-bottom: 15px;` : "")}

  background-color: white;

  ${({ alwaysPadding, scaleAmount }) =>
    alwaysPadding
      ? `
    .app-select__value-container {
      padding-top: ${unit(
        scaleAmount
          ? (theme.element.padding.large * 1.1) / scaleAmount
          : theme.element.padding.large * 1.1
      )};
    }
    `
      : ""}
  .app-select__control--is-focused {
    .app-select__value-container {
      padding-top: ${({ ignorePadding, scaleAmount }) =>
        !ignorePadding &&
        unit(
          scaleAmount
            ? (theme.element.padding.large * 1.1) / scaleAmount
            : theme.element.padding.large * 1.1
        )};
    }
  }
  /* Keeps the floating label's room above the value once a value is
     selected, independent of focus - react-select only adds this modifier
     class to the value container while hasValue is true. */
  .app-select__value-container--has-value {
    padding-top: ${({ ignorePadding, scaleAmount }) =>
      !ignorePadding &&
      unit(
        scaleAmount
          ? (theme.element.padding.large * 1.1) / scaleAmount
          : theme.element.padding.large * 1.1
      )};
  }

  ${Label} {
    z-index: 100;
    left: ${({ withIcon, labelPadding, scaleAmount }) => {
      let value = unit(theme.element.padding.large);
      if (withIcon && labelPadding && scaleAmount) {
        value = unit(labelPadding / scaleAmount);
      } else if (withIcon && labelPadding && !scaleAmount) {
        value = unit(labelPadding);
      } else if (withIcon && !labelPadding && !scaleAmount) {
        value = unit(theme.element.padding.large + INPUT_ICON_DEFAULTS.size);
      } else if (withIcon && !labelPadding && scaleAmount) {
        value = unit(
          (theme.element.padding.large + INPUT_ICON_DEFAULTS.size) / scaleAmount
        );
      } else if (!withIcon && labelPadding && scaleAmount) {
        value = unit(labelPadding / scaleAmount);
      } else if (!withIcon && !labelPadding && scaleAmount) {
        value = unit(theme.element.padding.large / scaleAmount);
      } else if (!withIcon && labelPadding && !scaleAmount) {
        value = unit(labelPadding);
      }
      return value;
    }};
    transition: all 350ms ease;
    ${({ alwaysPadding }) => (!!alwaysPadding ? LabelActive : "")}
  }
  .app-select__control--is-focused + ${Label} {
    ${LabelActive}
  }
`;

const SelectContainerExtended: React.ComponentType<
  ContainerProps & {
    icon?: any;
    title?: any;
    withIcon?: boolean;
    alwaysPadding?: boolean;
    ignorePadding?: boolean;
    iconPosition?: IconPositionType;
    labelPadding: number;
    scaleAmount?: number;
    errorMessage?: any;
  }
> = ({ icon, title, className, iconPosition: _iconPosition, ...props }) => {
  const iconPosition = _iconPosition || "left";

  return (
    <SelectContainerExtendedStyled
      className={`${className} ${SELECT_DEFAULTS.selectContainerClassName} `}
      {...props}
    >
      {icon && (
        <InputIcon iconPosition={iconPosition} scaleAmount={props.scaleAmount}>
          {" "}
          {icon}{" "}
        </InputIcon>
      )}

      <FieldError
        fontSize="smaller"
        color="white"
        bg="alert"
        errorMessage={props.errorMessage}
      >
        {props.errorMessage}
      </FieldError>

      {props.children}

      <Label
        className={props.hasValue ? "active" : ""}
        scaleAmount={props.scaleAmount}
      >
        {title}
      </Label>
    </SelectContainerExtendedStyled>
  );
};

const SelectCommonProps = {
  menuPortalTarget: document.body,
  classNamePrefix: SELECT_DEFAULTS.classNamePrefix,
};

const DropdownIcon = styled(Icon)`
  width: 26px;
  height: 26px;
`;

// react-select's own hasValue lags a render behind on mount, so a
// pre-filled select shows its label un-shrunk until the user touches it -
const resolveHasValue = (selectProps: any, fallback: boolean) => {
  const { value, defaultValue } = selectProps;
  const hasDefaultValue = Array.isArray(defaultValue)
    ? defaultValue.length > 0
    : defaultValue !== undefined && defaultValue !== null;
  const hasExplicitValue =
    (value !== undefined && value !== null) || hasDefaultValue;
  if (!hasExplicitValue) return fallback;
  return Array.isArray(value) ? value.length > 0 : true;
};

const StableSelectContainer = (containerProps: any) => {
  const { selectProps } = containerProps;
  return (
    <SelectContainerExtended
      title={selectProps.title}
      icon={selectProps.icon}
      withIcon={selectProps.withIcon}
      ignorePadding={selectProps.ignorePadding}
      alwaysPadding={selectProps.alwaysPadding}
      labelPadding={selectProps.labelPadding}
      scaleAmount={selectProps.scaleAmount}
      errorMessage={selectProps.errorMessage}
      {...containerProps}
      hasValue={resolveHasValue(selectProps, containerProps.hasValue)}
    />
  );
};

const StableValueContainer = (containerProps: any) => (
  <ValueContainer
    {...containerProps}
    hasValue={resolveHasValue(
      containerProps.selectProps,
      containerProps.hasValue
    )}
  />
);

const StableDropdownIndicator = () => (
  <Block padding={{ left: 12, right: 12 }}>
    <DropdownIcon name="dropdownIcon" color="gray" shade="darkest" />
  </Block>
);

const getSelectComponents = (): SelectComponentsConfig<
  any,
  boolean,
  GroupBase<any>
> => ({
  SelectContainer: StableSelectContainer,
  Option: StableOption,
  DropdownIndicator: StableDropdownIndicator,
  ValueContainer: StableValueContainer,
});

function SelectOptionComponent() {
  return (optionProps: React.PropsWithChildren<any>) => (
    <Option {...optionProps}>
      <Block
        display="grid"
        alignItems="center"
        gridGap={8}
        gridTemplate={{
          columns: optionProps.selectProps.optionWithIcon ? "20px 1fr" : "1fr",
        }}
      >
        {optionProps.selectProps.optionWithIcon && (
          <Block>
            <Icon name={optionProps.data.icon || "empty"} />
          </Block>
        )}
        <Block>
          {optionProps.children}
          {optionProps.selectProps.withDescription &&
            optionProps.data.description && (
              <Block fontSize="smaller" opacity={0.75} margin={{ top: 5 }}>
                {optionProps.data.description}
              </Block>
            )}
        </Block>
      </Block>
    </Option>
  );
}

const StableOption = SelectOptionComponent();

function SelectControlComponent(
  containerProps: React.PropsWithChildren<any>
) {
  const iconMode =
    containerProps.selectProps.withIcon && containerProps.selectProps.icon;

  return (
    <Control {...containerProps}>
      <Block
        style={{ width: "100%" }}
        padding={{ left: 4, right: 4 }}
        display="grid"
        alignItems="center"
        gridTemplate={{ columns: iconMode ? "20px 1fr" : "1fr" }}
      >
        {/* Icon */}
        {iconMode && <Block>{containerProps.selectProps.icon}</Block>}

        {/* Content */}
        <Block display="flex" margin={iconMode && { left: -8, right: -8 }}>
          {" "}
          {containerProps.children}
        </Block>
      </Block>
    </Control>
  );
}

const SelectMenuSectionTitle = styled(Block)`
  padding: 12px 0 0 16px;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
  text-transform: uppercase;
  white-space: nowrap;
`;

function SelectMenuComponent(
  props: any
):
  | React.ComponentClass<any, any>
  | React.FunctionComponent<any>
  | undefined {
  return (menuProps: React.PropsWithChildren<any>) => (
    <Menu {...menuProps}>
      <Block>
        <SelectMenuSectionTitle>{props.title}</SelectMenuSectionTitle>
        <Block>{menuProps.children}</Block>
      </Block>
    </Menu>
  );
}

function getSelectValue(
  primitiveValue: boolean | undefined,
  _value: any,
  options: any
) {
  // @TODO Improve typing
  const value = primitiveValue
    ? options.find((option: any) => String(option.value) === String(_value))
    : _value;
  return value as OptionType;
}

const ExtendedSelect: FunctionComponent<ISelectProps> = ({
  primitiveValue,
  value: _value,
  // Discarded: styled(Select) usages (e.g. SocialSelect) inject an app-shaped
  // `theme` prop via context. react-select's own `theme` prop expects its own
  // shape ({ colors, spacing, borderRadius }), so keep the two from mixing.
  theme: _appTheme,
  ...props
}) => {
  const value = getSelectValue(primitiveValue, _value, props.options);
  const components = {
    ...getSelectComponents(),
    ...props.components,
  };
  const styles: any = { ...SelectStyles, ...props.styles };
  const finalProps = {
    ...SelectCommonProps,
    ...props,
    components,
    styles,
    value,
  };
  return props.isClearable ? (
    <Creatable {...finalProps} />
  ) : (
    <Select {...finalProps} />
  );
};

const ExtendedAsyncSelect: FunctionComponent<IAsyncSelectProps> = ({
  primitiveValue,
  value: _value,
  // See the comment in ExtendedSelect above.
  theme: _appTheme,
  ...props
}) => {
  const value = getSelectValue(primitiveValue, _value, props.options);
  const components = {
    ...getSelectComponents(),
    ...props.components,
  };
  const styles: any = { ...SelectStyles, ...props.styles };
  const finalProps = {
    ...props,
    ...SelectCommonProps,
    components,
    styles,
    value,
  };
  return props.isClearable ? (
    <AsyncCreatable {...finalProps} />
  ) : (
    <Async {...finalProps} />
  );
};

const SelectTagsStyles: any = {
  menu: () => ({ display: "none", visibility: "hidden" }),
  valueContainer: (base: any) => ({ ...base, padding: "0 !important" }),
  control: (base: any, state: any) => ({
    ...SelectControlStyles(base, state),
    backgroundColor: "transparent !important",
    borderColor: "transparent !important",
  }),
  input: (base: React.CSSProperties) => ({
    ...SelectInputStyles(base),
    marginBottom: "10px !important",
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "#ff4156",
    padding: "10px !important",
    margin: "0",
    marginRight: "10px !important",
    marginBottom: "10px !important",
    borderRadius: "5px !important",
  }),
  multiValueLabel: (base: any) => ({ ...base, color: "white" }),
  multiValueRemove: (base: any) => ({
    ...base,
    backgroundColor: "transparent !important",
    "& svg": { fill: "white" },
    "&:hover": { backgroundColor: "transparent !important" },
  }),
};

const TagsSelectComponents = {
  DropdownIndicator: () => null,
  ClearIndicator: () => null,
  // MenuList: () => <span></span>,
};

const TagsSelect: FunctionComponent<ISelectProps> = (props) => {
  return (
    <ExtendedSelect
      isMulti
      isClearable
      styles={{ ...SelectTagsStyles, ...props.styles }}
      {...props}
      components={{
        ...TagsSelectComponents,
        ...props.components,
      }}
    />
  );
};
const TagsAsyncSelect: FunctionComponent<IAsyncSelectProps> = (props) => {
  return (
    <ExtendedAsyncSelect
      isMulti
      isClearable
      styles={{ ...SelectTagsStyles, ...props.styles }}
      {...props}
      components={{
        ...TagsSelectComponents,
        ...props.components,
      }}
    />
  );
};

export {
  ExtendedSelect as Select,
  ExtendedAsyncSelect as AsyncSelect,
  TagsSelect,
  TagsAsyncSelect,
  ISelectProps,
  IAsyncSelectProps,
  SelectStyles,
  SelectTagsStyles,
  SelectControlStyles,
  SelectOptionStyle,
  SELECT_DEFAULTS,
  INPUT_ICON_DEFAULTS,
  INPUT_BORDER_COLOR,
  SelectCommonProps,
  SelectOptionComponent,
  SelectMenuComponent,
  SelectControlComponent,
  getSelectComponents,
};
